import React, { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { supabaseFetch } from '../../lib/supabase';
import { userActionService } from '../../data/dataService';
import ConfirmationModal from '../core/ConfirmationModal';
import { useTranslation } from '../../i18n/useTranslation';
import { parseEventDate } from '../../utils/eventDateTime';
import { useTagHouseFallback } from '../../hooks/useTagHouseFallback';

const EventsGrid = ({
  onEventClick,
  context,
  events,
  userId,
  isMobile = false,
  selectedGroup = null
}) => {
  const themeConfig = useTheme();
  const { t, formattingLocale } = useTranslation('events');
  // R4 house fallback (image-layer build) — null until fetched; each <img>
  // below keeps its previous hardcoded default as the interim/last-resort.
  const houseFallback = useTagHouseFallback();
  const [eventsWithDetails, setEventsWithDetails] = useState([]);
  const [attendingEvents, setAttendingEvents] = useState({});
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });
  console.log('🟡 EventsGrid rendering with events:', events?.length || 0);
  console.log('🟡 EventsGrid events array:', events);

  // Filter articles by article_type = 'event'
  const eventsList = (events || []).filter(event => event.article_type === 'event');

  // Helper: get a date-only string (YYYY-MM-DD) in local timezone for grouping
  const getLocalDateString = (dateStr) => {
    if (!dateStr) return null;
    const d = parseEventDate(dateStr);
    if (!d) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };


  // Helper: format a date divider label (e.g. "Friday, 30 May 2025") — uses app locale
  const formatDateDivider = (dateStr) => {
    if (!dateStr) return '';
    const d = parseEventDate(dateStr);
    if (!d) return '';
    return d.toLocaleDateString(formattingLocale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Filter out past events and sort by start_date ascending
  const now = new Date();
  const sortedActiveEvents = [...eventsWithDetails]
    .filter(event => {
      // Use end_date if available, otherwise fall back to start_date
      const cutoffDate = event.end_date ? parseEventDate(event.end_date) : (event.start_date ? parseEventDate(event.start_date) : null);
      // Keep events with no dates (TBD), and events that haven't finished yet
      return !cutoffDate || cutoffDate > now;
    })
    .sort((a, b) => {
      // Events without start_date go to the end
      if (!a.start_date && !b.start_date) return 0;
      if (!a.start_date) return 1;
      if (!b.start_date) return -1;
      return parseEventDate(a.start_date) - parseEventDate(b.start_date);
    });

  // Group events by date for divider rendering
  const groupEventsByDate = (events) => {
    const groups = [];
    let currentDateKey = null;
    for (const event of events) {
      const dateKey = getLocalDateString(event.start_date) || 'tbd';
      if (dateKey !== currentDateKey) {
        currentDateKey = dateKey;
        groups.push({
          dateKey,
          label: dateKey === 'tbd' ? t('tbd') : formatDateDivider(event.start_date),
          events: []
        });
      }
      groups[groups.length - 1].events.push(event);
    }
    return groups;
  };

  // Fetch event_details for all events
  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!eventsList.length) {
        setEventsWithDetails([]);
        return;
      }

      try {
        // Get all event IDs
        const eventIds = eventsList.map(e => e.id);

        // Fetch event_details for all events in one query (direct fetch, supabase client can hang)
        const { data: eventDetailsData, error } = await supabaseFetch.query('event_details', {
          select: '*',
          inFilter: { article_id: eventIds }
        });

        if (error) {
          console.error('Error fetching event details:', error);
          setEventsWithDetails(eventsList);
          return;
        }

        // Merge event_details with events
        const mergedEvents = eventsList.map(event => {
          const details = eventDetailsData?.find(d => d.article_id === event.id);
          return {
            ...event,
            start_date: details?.start_date,
            end_date: details?.end_date,
            venue_name: details?.venue_name,
            venue_address: details?.venue_address,
            venue_postcode: details?.venue_postcode,
            max_attendees: details?.max_attendees,
            contact_email: details?.contact_email,
            contact_phone: details?.contact_phone,
            current_attendees: details?.current_attendees || 0
          };
        });

        setEventsWithDetails(mergedEvents);
      } catch (error) {
        console.error('Error fetching event details:', error);
        setEventsWithDetails(eventsList);
      }
    };

    fetchEventDetails();
  }, [events]);

  // Load attendance status for all events
  useEffect(() => {
    const loadAttendanceStatus = async () => {
      if (!userId || !eventsWithDetails.length) return;

      try {
        const attendanceMap = {};

        for (const event of eventsWithDetails) {
          const isAttending = await userActionService.isAttendingEvent(userId, event.id);
          attendanceMap[event.id] = isAttending;
        }

        setAttendingEvents(attendanceMap);
      } catch (error) {
        console.error('Error loading attendance status:', error);
      }
    };

    loadAttendanceStatus();
  }, [userId, eventsWithDetails]);

  // Helper function to format date and time from Supabase format
  const formatEventDateTime = (startDate) => {
    if (!startDate) return { date: t('tbd'), time: t('tbd') };

    const eventDate = parseEventDate(startDate);
    if (!eventDate) return { date: t('tbd'), time: t('tbd') };
    const dateStr = eventDate.toLocaleDateString(formattingLocale, {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
    const timeStr = eventDate.toLocaleTimeString(formattingLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    return { date: dateStr, time: timeStr };
  };

  // Fixed event handlers
  const handleEventClick = (event) => {
    console.log('handleEventClick called with:', event);
    console.log('onEventClick prop:', onEventClick);
    if (onEventClick) {
      console.log('Calling onEventClick...');
      onEventClick(event);
    } else {
      console.log('onEventClick prop is missing!');
    }
  };

  const handleJoinLeaveEvent = async (eventId, eventTitle, isCurrentlyAttending) => {
    console.log('Join/Leave event:', eventId, eventTitle, 'Currently attending:', isCurrentlyAttending);

    if (!userId) {
      setModal({
        visible: true,
        message: t('please_log_in_to_manage_attendance'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    if (isCurrentlyAttending) {
      // User wants to leave the event
      const result = await userActionService.leaveEvent(userId, eventId);

      if (result === true) {
        // Success - update local state
        setAttendingEvents(prev => ({
          ...prev,
          [eventId]: false
        }));
        setModal({
          visible: true,
          message: t('you_have_left_event', { eventTitle }),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      } else {
        // Error
        setModal({
          visible: true,
          message: t('failed_to_leave_event'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      }
    } else {
      // User wants to join the event
      const result = await userActionService.attendEvent(userId, eventId);

      if (result === true) {
        // Success - update local state
        setAttendingEvents(prev => ({
          ...prev,
          [eventId]: true
        }));
        setModal({
          visible: true,
          message: t('successfully_joined_event', { eventTitle }),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      } else {
        // Error
        setModal({
          visible: true,
          message: t('failed_to_join_event'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      }
    }
  };

  // Navigate to edit event
  const handleEditEvent = (event) => {
    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: { view: 'new-event', mode: 'edit', article: event }
    }));
  };

  // Mobile Event Card Component - fixed 160px size
  const MobileEventCard = ({ event }) => {
    const { time } = formatEventDateTime(event.start_date);
    const isAttending = attendingEvents[event.id];
    const isAuthor = userId && event.author_id === userId;

    return (
      <div
        onClick={() => handleEventClick(event)}
        style={{
          width: '160px',
          height: '160px',
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Image section - 60% height */}
        <div style={{
          flex: '3',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <img
            src={event.cover_image_url || houseFallback || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=300&h=200&fit=crop'}
            alt={event.title}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              // §5a — illustrations carry their own drawn border at ~3:2; never
              // crop. contain + the card's own background (white, matching
              // this card's backgroundColor two levels up) as the letterbox
              // fill, so the border reads as a uniform frame, not a crop.
              objectFit: 'contain',
              backgroundColor: 'white'
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = houseFallback || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=300&h=200&fit=crop';
            }}
          />
          {/* Author edit badge */}
          {isAuthor && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditEvent(event);
              }}
              style={{
                position: 'absolute',
                top: '8px',
                left: '8px',
                backgroundColor: themeConfig.primary,
                color: 'white',
                padding: '4px 8px',
                borderRadius: '8px',
                fontSize: '10px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {t('edit_event')}
            </button>
          )}
          {/* Attending badge */}
          {isAttending && (
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              backgroundColor: '#10b981',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '8px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              {t('attending')}
            </div>
          )}
          {/* View count badge */}
          {event.view_count > 0 && (
            <div style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '3px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              <Eye size={10} />
              {event.view_count}
            </div>
          )}
        </div>

        {/* Content section - 40% height */}
        <div style={{
          flex: '2',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div style={{
            fontWeight: '600',
            fontSize: '12px',
            color: '#1f2937',
            lineHeight: '1.3',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {event.title}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '4px',
            marginTop: '4px'
          }}>
            <span style={{
              fontSize: '8px',
              fontWeight: '700',
              color: '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t('starts')}
            </span>
            <span style={{
              fontSize: '14px',
              fontWeight: '700',
              color: themeConfig.primary
            }}>
              {time}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Mobile Layout - fixed-size cards with auto-fill columns
  // Bottom padding ensures content is visible above floating footer buttons
  if (isMobile) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        padding: '12px',
        paddingBottom: '80px', // Space for floating footer buttons
        overflowY: 'auto'
      }}>

        {sortedActiveEvents.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            {t('no_events_found')}
          </div>
        ) : (
          <div>
            {/* Group image as first card on mobile */}
            {isMobile && selectedGroup?.logo_url && (
              <div style={{
                width: '160px',
                aspectRatio: '1 / 1',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                backgroundColor: '#FFFFFF',
                margin: '0 auto 12px auto'
              }}>
                <img
                  src={selectedGroup.logo_url}
                  alt={selectedGroup.name}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            )}
            {groupEventsByDate(sortedActiveEvents).map((group) => (
              <div key={group.dateKey}>
                {/* Date divider */}
                <div style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: '#6b7280',
                  padding: '8px 4px 4px 4px',
                  lineHeight: '1.2'
                }}>
                  {group.label}
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, 160px)',
                  gap: '12px',
                  justifyContent: 'center',
                  marginBottom: '8px'
                }}>
                  {group.events.map((event) => (
                    <MobileEventCard key={event.id} event={event} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Confirmation Modal */}
        {modal.visible && (
          <ConfirmationModal
            visible={modal.visible}
            message={modal.message}
            onOk={modal.onOk}
            showCancel={false}
          />
        )}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div
    style={{
      width: '100%',
      height: '100%',
      backgroundColor: 'transparent',
      overflow: 'hidden',
      position: 'relative'
    }}
    >
    {/* Centered events container */}
    <div
    style={{
      position: 'absolute',
      top: '60px',
      left: '20px',
      right: '20px',
          height: 'calc(100vh - 160px)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          justifyContent: 'flex-start',
          overflowY: 'auto',
          overflowX: 'visible'
    }}
    >
    {sortedActiveEvents.length === 0 ? (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        color: '#6b7280',
        fontSize: '16px'
      }}>
        {t('no_events_found_desktop')}
      </div>
    ) : (
      groupEventsByDate(sortedActiveEvents).map((group) => (
        <div key={group.dateKey}>
          {/* Date divider */}
          <div style={{
            fontSize: '17px',
            fontWeight: '600',
            color: '#6b7280',
            padding: '6px 0 2px 0',
            lineHeight: '1.2'
          }}>
            {group.label}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 340px)',
            gap: '8px'
          }}>
          {group.events.map((event) => {
            const { time } = formatEventDateTime(event.start_date);
            const isAuthor = userId && event.author_id === userId;

            return (
              <div
                key={event.id}
                onClick={() => handleEventClick(event)}
                style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  width: '340px',
                  padding: '0',
                  backgroundColor: 'white',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  height: '82px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                {/* Column 1: Event Image */}
                <div style={{ width: '82px', flexShrink: 0 }}>
                  <img
                    src={event.cover_image_url || houseFallback || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=150&h=90&fit=crop'}
                    alt={event.title}
                    loading="lazy"
                    style={{
                      width: '82px',
                      height: '82px',
                      // §5a — never crop the illustration's own border; contain
                      // + this card's background (white, per its container
                      // backgroundColor above) as the letterbox fill.
                      objectFit: 'contain',
                      backgroundColor: 'white',
                      borderRadius: '0'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = houseFallback || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=150&h=90&fit=crop';
                    }}
                  />
                </div>

                {/* Column 2: Title + Location — wraps to 2 lines each */}
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  padding: '5px'
                }}>
                  <div style={{
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#1f2937',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {event.title}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    lineHeight: '1.3',
                    marginTop: '2px',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {event.venue_name || event.venue_address || event.group_name || event.author_name || t('event_organizer')}
                  </div>
                </div>

                {/* Column 3: STARTS + Time */}
                <div style={{
                  width: '80px',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 5px'
                }}>
                  <div style={{
                    fontSize: '9px',
                    fontWeight: '700',
                    color: '#9ca3af',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    lineHeight: '1'
                  }}>
                    {t('starts')}
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: themeConfig.primary,
                    lineHeight: '1.2'
                  }}>
                    {time}
                  </div>
                </div>

                {/* Edit button for author */}
                {isAuthor && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditEvent(event);
                    }}
                    style={{
                      width: '62px',
                      flexShrink: 0,
                      height: '82px',
                      backgroundColor: themeConfig.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '0 20px 20px 0',
                      padding: '0',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = themeConfig.accent;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = themeConfig.primary;
                    }}
                  >
                    {t('edit_event')}
                  </button>
                )}

                {/* Attending badge - small indicator (non-authors only) */}
                {!isAuthor && attendingEvents[event.id] && (
                  <div style={{
                    width: '8px',
                    flexShrink: 0,
                    backgroundColor: '#10b981',
                    borderRadius: '0 20px 20px 0'
                  }} />
                )}
              </div>
            );
          })}
          </div>
        </div>
      ))
    )}
    </div>

    {/* Confirmation Modal */}
    {modal.visible && (
      <ConfirmationModal
        visible={modal.visible}
        message={modal.message}
        onOk={modal.onOk}
        showCancel={false}
      />
    )}
    </div>
  );
};

export default EventsGrid;
